/**
 * Cancel / reject reasons are stored as free text or as preset codes
 * ("customer request", "item_unavailable"). Every screen renders them through this
 * one map so a preset never shows up as raw English code in a Chinese/Malay UI.
 * Unknown (typed-in) reasons are returned untouched.
 */
const REASONS = {
    customer_request: { zh: "顾客要求取消", en: "Customer request", ms: "Permintaan pelanggan" },
    item_unavailable: { zh: "菜品缺货", en: "Item unavailable", ms: "Item tiada stok" },
    wrong_order: { zh: "下错单", en: "Wrong order", ms: "Pesanan salah" },
    system_issue: { zh: "系统问题", en: "System issue", ms: "Masalah sistem" },
    duplicate_order: { zh: "重复下单", en: "Duplicate order", ms: "Pesanan berganda" },
    out_of_stock: { zh: "缺货", en: "Out of stock", ms: "Kehabisan stok" },
    stall_closed: { zh: "档口已打烊", en: "Stall closed", ms: "Gerai tutup" },
    payment_failed: { zh: "付款失败", en: "Payment failed", ms: "Pembayaran gagal" },
    store_busy: { zh: "档口太忙，暂时无法出餐", en: "Stall too busy", ms: "Gerai terlalu sibuk" },
    cancelled_by_customer: { zh: "顾客自行取消", en: "Cancelled by the customer", ms: "Dibatalkan oleh pelanggan" },
    cancelled_at_pos: { zh: "门店取消", en: "Cancelled by the store", ms: "Dibatalkan oleh kedai" },
    others: { zh: "其他", en: "Others", ms: "Lain-lain" },
    // written by the one-off clean-up scripts ("Closed: stale test order") — never shown as raw English
    closed_stale: { zh: "订单长时间未处理，系统已关闭", en: "Closed: no action for a long time", ms: "Ditutup: tiada tindakan sekian lama" },
};
const AUTO_TEXT = {
    cancel: { zh: "取消订单自动生成退款", en: "Auto-created on order cancellation", ms: "Dicipta automatik semasa pembatalan pesanan" },
    reject: { zh: "档口拒绝餐品，自动生成退款", en: "Auto-created: stall rejected item", ms: "Dicipta automatik: gerai menolak item" },
};
const norm = (r) => r.trim().toLowerCase().replace(/[\s-]+/g, "_");
/** "item unavailable: tomato ran out" — a preset plus the cashier's note */
function presetWithNote(reason) {
    const m = /^([A-Za-z_ -]{3,30}):\s+(.+)$/s.exec(reason.trim());
    return m && REASONS[norm(m[1])] ? { code: norm(m[1]), note: m[2].trim() } : null;
}
export function cancelReasonLabel(reason, lang = "zh") {
    if (!reason)
        return "";
    const l0 = lang.startsWith("ms") ? "ms" : lang.startsWith("en") ? "en" : "zh";
    // System-written refund reasons: "Auto-created on order cancellation: <reason>" / 'Auto-created: stall rejected item "<name>" (<reason>)'
    const auto = /^Auto-created on order cancellation(?:: (.*))?$/s.exec(reason);
    if (auto)
        return AUTO_TEXT.cancel[l0] + (auto[1] ? `${l0 === "zh" ? "：" : ": "}${cancelReasonLabel(auto[1], lang)}` : "");
    const rej = /^Auto-created: stall rejected item(?: "(.*)")?(?: \((.*)\))?$/s.exec(reason);
    if (rej)
        return AUTO_TEXT.reject[l0] + (rej[1] ? ` "${rej[1]}"` : "") + (rej[2] ? (l0 === "zh" ? `（${cancelReasonLabel(rej[2], lang)}）` : ` (${cancelReasonLabel(rej[2], lang)})`) : "");
    if (/^Closed: stale/i.test(reason.trim()))
        return REASONS.closed_stale[l0];
    if (/^Cancelled at POS$/i.test(reason.trim()))
        return REASONS.cancelled_at_pos[l0];
    if (/^Cancelled by customer$/i.test(reason.trim()))
        return REASONS.cancelled_by_customer[l0];
    const pn = presetWithNote(reason);
    if (pn)
        return `${REASONS[pn.code][l0]}${l0 === "zh" ? "：" : ": "}${pn.note}`;
    const key = norm(reason);
    const hit = REASONS[key];
    if (!hit)
        return reason;
    const l = lang.startsWith("ms") ? "ms" : lang.startsWith("en") ? "en" : "zh";
    return hit[l];
}
/** Canonical preset code for a preset typed in any spacing/case ("customer request" → "customer_request"); free text is returned untouched. */
export function cancelReasonCode(reason) {
    const key = reason.trim().toLowerCase().replace(/[\s-]+/g, "_");
    return REASONS[key] ? key : reason;
}
/**
 * What a CUSTOMER should read as the reason for a store-side cancellation. "Customer request" typed by a cashier
 * (the customer phoned the counter) would contradict the "cancelled by the store" headline, so that preset — and the
 * generic placeholders — show no reason line at all.
 */
export function customerFacingCancelReason(reason, lang = "zh") {
    // Never a blank line: a cancelled order always says why. The generic placeholders and the "customer request" typed by a
    // cashier read as "cancelled by the store" (the headline says the same, so no contradiction); a missing reason reads "Others".
    if (!reason || !reason.trim())
        return cancelReasonLabel("others", lang);
    const pn = presetWithNote(reason);
    if (pn?.code === "customer_request")
        return `${cancelReasonLabel("cancelled_at_pos", lang)}${/^(en|ms)/.test(lang) ? ": " : "："}${pn.note}`;
    if (reason === "Cancelled at POS" || cancelReasonCode(reason) === "customer_request")
        return cancelReasonLabel("cancelled_at_pos", lang);
    return cancelReasonLabel(reason, lang);
}
//# sourceMappingURL=cancelReason.js.map